import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { ArrowRight, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog?published_only=true`);
        setPosts(response.data);
      } catch (error) {
        console.error("Failed to fetch blog posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []); // Empty deps is correct - only run once on mount

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      );
    }
    
    if (posts.length === 0) {
      return (
        <div
          data-testid="blog-empty-state"
          className="text-center py-16 bg-[#F3F0E9] border border-[#E2E8F0]"
        >
          <p className="font-serif text-2xl text-[#1A202C] mb-4">
            No posts yet
          </p>
          <p className="font-sans text-[#4A5568]">
            Check back soon for updates and reflections.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {posts.map((post) => (
          <article
            key={post.id}
            data-testid={`blog-post-card-${post.slug}`}
            className="blog-card p-8 group"
          >
            <div className="flex items-center gap-4 text-sm text-[#718096] mb-4">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span>·</span>
              <span>{post.author}</span>
            </div>

            <Link to={`/blog/${post.slug}`}>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#1A202C] mb-4 group-hover:text-[#2C4C5B] transition-colors">
                {post.title}
              </h2>
            </Link>

            <p className="font-sans text-[#4A5568] leading-relaxed mb-6 line-clamp-3">
              {post.excerpt}
            </p>

            <Link
              to={`/blog/${post.slug}`}
              className="inline-flex items-center gap-2 font-sans text-sm font-medium text-[#D4AF37] hover:text-[#B3932E] transition-colors group"
            >
              Read more
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </article>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="blog-page" className="min-h-screen bg-[#FAF9F6]">
      <Navigation />

      <main className="pt-32 pb-24 px-6 sm:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#B3932E] font-sans mb-4">
              Blog & Updates
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1A202C] mb-4">
              Reflections & News
            </h1>
            <p className="font-sans text-lg text-[#4A5568] max-w-2xl">
              Updates, reflections, and insights from Linda Gallagher and Omlight Publications.
            </p>
          </div>

          {/* Posts List */}
          {renderContent()}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
